<template>
  <div class="document-editor">
    <div id="quill-container"></div>
    <transition name="document-loading">
      <loading-message
        :message="$t('session.documentEditor.loadingMessage')"
        class="document-loading document-loading--connection"
        v-show="isLoading"
      />
    </transition>
    <transition name="document-loading">
      <loading-message
        :message="$t('session.documentEditor.isConnectingLoadingMessage')"
        class="document-loading document-loading--connection"
        v-show="isConnecting"
      />
    </transition>
    <refresh-document-editor-modal v-if="showRefreshModal" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
  ImageCompressor,
  maxImagesEventName,
  fileSizeTooBigEventName,
  MAX_TOTAL_IMAGES,
} from '@/utils/quill-image-optimizer'
import Quill from 'quill'
import QuillCursors from 'quill-cursors'
import LoadingMessage from '@/components/LoadingMessage.vue'
import RefreshDocumentEditorModal from '@/views/SessionView/RefreshDocumentEditorModal.vue'

Quill.register('modules/cursors', QuillCursors)
Quill.register('modules/image', ImageCompressor)
const Delta = Quill.import('delta')

export default {
  components: {
    LoadingMessage,
    RefreshDocumentEditorModal,
  },
  props: {
    // Task 02 / STEP-11A (BLOCKER-1): mirrors Whiteboard.vue's existing
    // `sessionId` prop. SessionView already resolves this synchronously,
    // before either child mounts -- it is the one reliable, ordering-
    // independent source of "which session is this editor for", unlike
    // `state.user.session` (Vuex), whose update from THIS SAME session-change
    // event race depends on listener registration order against App/index.vue's
    // own independent 'session-change' handler.
    sessionId: { type: String, required: true },
  },
  data() {
    return {
      quillEditor: null,
      // set default loading state
      isLoading: true,
      incomingDeltas: [],
      retries: 0,
      showRefreshModal: false,
      isConnecting: false,
      // Task 02 / STEP-11A (BLOCKER-1): guards the ONE-TIME initial
      // requestQuillState -- see the 'session-change' handler below.
      // retryLoadingDoc's own re-request is independent of this flag.
      hasRequestedInitialState: false,
    }
  },
  computed: {
    ...mapState({
      currentSession: state => state.user.session,
      isSessionConnectionAlive: state => state.user.isSessionConnectionAlive,
    }),
    ...mapGetters({
      isVolunteer: 'user/isVolunteer',
    }),
  },
  mounted() {
    const toolbar = [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ]

    if (!this.isVolunteer) toolbar.push(['image'])

    this.quillEditor = new Quill('#quill-container', {
      placeholder: this.$root.$t(
        'session.documentEditor.quillEditorPlaceholder'
      ),
      theme: 'snow',
      formats: [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'list',
        'image',
      ],
      modules: {
        image: {
          quality: 0.8,
          maxWidth: 1000,
          maxHeight: 1000,
          imageType: 'image/webp',
        },
        cursors: {
          selectionChangeSource: 'cursor-api',
          transformOnTextChange: true,
        },
        toolbar,
      },
    })

    this.quillEditor.root.addEventListener(
      maxImagesEventName,
      () =>
        alert(
          this.$root.$t('session.documentEditor.maxImagesEventNameAlert', {
            maxTotalImages: MAX_TOTAL_IMAGES,
          })
        ),
      false
    )
    this.quillEditor.root.addEventListener(
      fileSizeTooBigEventName,
      () =>
        alert(
          this.$root.$t('session.documentEditor.fileSizeTooBigEventNameAlert')
        ),
      false
    )

    if (this.isVolunteer) {
      const useHandler = () => {
        const delta = new Delta()
        return delta.insert('')
      }
      this.quillEditor.clipboard.addMatcher('IMG', useHandler)
      this.quillEditor.clipboard.addMatcher('PICTURE', useHandler)
    }

    // do not allow user to make edits until the quill doc contents are set
    this.quillEditor.disable()

    this.quillEditor.on('text-change', this.quillTextChange)
    this.quillEditor.on('selection-change', this.quillSelectionChange)

    // Task 02 / STEP-11A (BLOCKER-1): the initial requestQuillState is no
    // longer emitted here. Backend authorization (R-15 Family 2) requires
    // `socket.data.currentSessionId` to already equal this session, which is
    // only set once this socket's `join` has genuinely succeeded server-side
    // -- a real network round trip that this component's own synchronous
    // mount can, and structurally does, finish well ahead of (see the
    // 'session-change' handler below, which is the actual confirmation).

    this.quillEditor
      .getModule('cursors')
      .createCursor('partnerCursor', 'Partner', '#16D2AA')
  },
  methods: {
    quillTextChange(delta, oldDelta, source) {
      if (source === 'user') {
        this.$socket.emit('transmitQuillDelta', {
          sessionId: this.currentSession._id,
          delta,
        })
      }
    },

    quillSelectionChange(range, oldRange, source) {
      if (source === 'user') {
        this.$socket.emit('transmitQuillSelection', {
          sessionId: this.currentSession._id,
          range,
        })
      }
    },
    updateContents(delta) {
      this.quillEditor.updateContents(delta)
    },
    emptyIncomingDeltas() {
      for (const delta of this.incomingDeltas) {
        this.updateContents(delta)
      }
    },
  },
  sockets: {
    // Task 02 / STEP-11A (BLOCKER-1): the trustworthy confirmation that
    // THIS socket's join for THIS session has succeeded server-side --
    // `sockets.ts`'s `join` handler sets `socket.data.currentSessionId`
    // strictly before emitting this (Task 02 / STEP-09B), so receiving a
    // matching `session-change` is sufficient proof the backend's
    // requestQuillState authorization guard (R-15 Family 2) will now accept
    // a request for `sessionId`. Matched against the EVENT PAYLOAD's own
    // `_id`/`id`, not `this.currentSession` (Vuex): `App/index.vue` also
    // listens for this same event and independently updates `state.user
    // .session` on it, and vue-socket.io does not guarantee listener
    // ordering between components, so relying on Vuex here could read a
    // still-stale value depending on registration order.
    'session-change'(sessionData) {
      if (this.hasRequestedInitialState) return
      if (
        sessionData._id !== this.sessionId &&
        sessionData.id !== this.sessionId
      )
        return
      this.hasRequestedInitialState = true
      this.$socket.emit('requestQuillState', { sessionId: this.sessionId })
    },

    quillState({ delta }) {
      this.quillEditor.setContents(delta)
      this.emptyIncomingDeltas()
      this.isLoading = false
      this.quillEditor.enable()
    },

    partnerQuillDelta({ delta }) {
      if (this.isLoading) this.incomingDeltas.push(delta)
      else this.updateContents(delta)
    },

    quillPartnerSelection({ range }) {
      this.quillEditor.getModule('cursors').moveCursor('partnerCursor', range)
    },

    /**
     *
     * This event lets us know the last delta that was composed to the Quill
     * document in our server cache
     *
     * If the last delta stored is found in our `incomingDeltas` queue,
     * that means the requested quill state from our server contains
     * the last delta stored and the ones before it. Remove those from
     * `incomingDeltas` to avoid appending duplicate deltas to the client Quill doc
     *
     */
    lastDeltaStored({ delta }) {
      if (delta) {
        const queueCutoff = this.incomingDeltas.findIndex(
          pendingDelta => pendingDelta.id === delta.id
        )
        this.incomingDeltas = this.incomingDeltas.slice(queueCutoff + 1)
      }
    },

    retryLoadingDoc() {
      const maxRetries = 10
      if (this.retries > maxRetries) {
        this.showRefreshModal = true
      } else {
        this.retries++
        this.$socket.emit('requestQuillState', {
          sessionId: this.currentSession._id,
        })
      }
    },
  },
  watch: {
    isSessionConnectionAlive(newValue, oldValue) {
      if (newValue && !oldValue) {
        // socket.io just reconnected, allow edits to the document editor
        this.quillEditor.enable()
        this.isConnecting = false
      } else {
        this.quillEditor.disable()
        this.isConnecting = true
      }
    },
  },
}
</script>

<style lang="scss">
.document-editor {
  height: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  position: relative;

  .ql-container.ql-snow {
    overflow: scroll;
    border: none;
  }

  .ql-toolbar.ql-snow {
    border-width: 0 0 1px 0;
    border-color: $c-border-grey;
  }

  .ql-cursor-flag {
    display: none;
  }
}

.document-loading {
  width: 100%;
  background-color: $c-shadow-warn;
  color: #fff;
  font-weight: normal;
  min-height: 40px;
  // !important is used to override the position specified in the LoadingMessage component
  position: absolute !important;
  left: 0;
  top: 40px;
  padding: 12px;
  z-index: 1000;
  transition: all 0.15s ease-in;
  text-align: center;

  &--connection {
    background-color: rgba(110, 140, 171, 0.87);
  }
}

html.is-rtl {
  .document-editor {
    text-align: left;
  }

  .document-loading {
    right: 0;
    left: unset;
  }
}
</style>
